'use client';

import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { trackCTAClick } from '@/lib/analytics';
import KSeFPromoBanner from './KSeFPromoBanner';

interface HeroContent {
  title: string;
  subtitle: string;
}

export default function HeroSection() {
  const [content, setContent] = useState<HeroContent>({
    title: `Migracje BaseLinker → Apilo
    z zachowaniem struktury i automatyzacji.
    
    Wdrożenia BaseLinker i Apilo`,
    subtitle: 'Porządkujemy sprzedaż wielokanałową i wdrażamy automatyzacje, które oszczędzają dziesiątki godzin miesięcznie.',
  });
  const [stats, setStats] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase
          .from('hero_stats')
          .select('*')
          .eq('is_active', true)
          .order('order_position', { ascending: true });

        if (error) {
          console.error('Error fetching hero stats:', error.message);
        } else {
          const formattedStats = data?.map((stat) => ({
            value: stat.stat_value,
            label: stat.stat_label,
          })) || [];
          setStats(formattedStats);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [supabase]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['hero_title', 'hero_subtitle']);

        if (error) {
          console.error('Error fetching hero content:', error.message);
          return;
        }

        const settingsMap: Record<string, string> = {};
        data?.forEach((setting) => {
          settingsMap[setting.key] = setting.value;
        });

        setContent({
          title: settingsMap['hero_title'] || content.title,
          subtitle: settingsMap['hero_subtitle'] || content.subtitle,
        });
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchContent();
  }, [supabase]);

  const handleCTAClick = (ctaName: string) => {
    trackCTAClick(ctaName, 'hero_section');
  };

  return (
    <section
      className="relative bg-navy overflow-hidden pt-4 pb-20 md:pt-6 md:pb-28"
      aria-label="Hero"
    >
      {/* Background grid texture */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
        aria-hidden="true"
      />
      {/* Glow accent */}
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-5 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #1D6FEB 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Eyebrow */}
        <div className="flex items-center gap-4 mb-4">
          <span className="hero-eyebrow text-blue-DEFAULT">
            Wdrożenia &amp; Migracje
          </span>
          <div className="h-px w-12 bg-blue-DEFAULT opacity-60" />
          <span className="hero-eyebrow text-white/30">
            BaseLinker · Apilo
          </span>
        </div>

        {/* Main headline */}
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <h1 className="text-white max-w-3xl font-800 tracking-tight font-normal text-[1.75rem] leading-[1.3] md:text-[2.25rem] md:leading-[1.25] mt-[46px] mb-[7px] pt-0" style={{ fontFamily: 'Inter, sans-serif' }}>
              {content.title.split('<br>').map((line, index) => (
                <React.Fragment key={index}>
                  {line.trim()}
                  {index < content.title.split('<br>').length - 1 && (
                    <>
                      <br />
                      {index === 1 && <br />}
                    </>
                  )}
                </React.Fragment>
              ))}
            </h1>
            <p className="text-base md:text-lg text-white/60 leading-relaxed font-400 max-w-md mt-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              {content.subtitle}
            </p>

            {/* KSeF Promotional Banner */}
            <div className="mt-8">
              <KSeFPromoBanner />
            </div>
          </div>

          <div className="lg:col-span-5 space-y-8">
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
              {stats?.map((stat) => (
                <div key={stat?.label}>
                  <div className="text-2xl font-800 text-white tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {stat?.value}
                  </div>
                  <div className="text-xs font-700 uppercase tracking-widest text-white/30 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {stat?.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-4 pt-2">
              <a 
                href="#kontakt" 
                className="inline-flex items-center justify-center gap-2 bg-blue-DEFAULT text-white font-800 text-xs sm:text-sm px-3 py-1.5 sm:px-6 rounded-xl transition-all duration-300 ease-out hover:bg-blue-light hover:scale-105 hover:shadow-lg active:scale-95 h-auto sm:h-11 max-w-full break-words" 
                style={{ fontFamily: 'Inter, sans-serif' }}
                onClick={() => handleCTAClick('Bezpłatna konsultacja')}
              >
                Bezpłatna konsultacja
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a 
                href="#pakiety" 
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white font-800 text-sm px-6 rounded-xl transition-all duration-300 ease-out hover:bg-white hover:text-navy hover:scale-105 hover:shadow-lg active:scale-95 h-11" 
                style={{ fontFamily: 'Inter, sans-serif' }}
                onClick={() => handleCTAClick('Zobacz pakiety')}
              >
                Zobacz pakiety
              </a>
            </div>
          </div>
        </div>

        {/* Platform badges */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap items-center gap-6">
          <span className="text-xs font-700 uppercase tracking-widest text-white/20" style={{ fontFamily: 'Inter, sans-serif' }}>
            Certyfikowani partnerzy:
          </span>
          {['BaseLinker', 'Apilo', 'Amazon SP-API', 'Allegro REST']?.map((p) => (
            <span
              key={p}
              className="px-4 py-2 rounded-full border border-white/15 text-xs font-700 uppercase tracking-wider text-white/50"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}